import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Sidebar list of registered Fusion transaction attributes for a BPM category.
 */
export default function BpmTransactionAttributesPanel({
  api,
  category = 'absence',
  onPickAttribute,
  disabled = false,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState(null);
  const [attributes, setAttributes] = useState([]);

  const load = useCallback(async () => {
    if (!category) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/copilot/bpm/attributes', {
        params: { category },
        validateStatus: () => true,
      });
      if (res.status === 404) {
        throw new Error(
          'GET /api/copilot/bpm/attributes not found (404). Restart API from latest main (scripts/restart-dev.ps1).'
        );
      }
      if (res.status !== 200) {
        throw new Error(res.data?.error || `HTTP ${res.status}`);
      }
      setMeta({
        label: res.data?.label,
        transactionHint: res.data?.transactionHint,
      });
      setAttributes(res.data?.attributes || []);
    } catch (err) {
      setMeta(null);
      setAttributes([]);
      setError(err.message || 'Failed to load attributes');
    } finally {
      setLoading(false);
    }
  }, [api, category]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <aside className="rounded-lg border border-cyan-200 bg-white/90 p-3 text-xs space-y-2 max-h-[min(420px,50vh)] overflow-y-auto">
      <div>
        <p className="font-semibold text-cyan-900">Transaction attributes</p>
        <p className="text-[10px] text-slate-600 mt-0.5">
          {meta?.label || category}
          {meta?.transactionHint ? ` · ${meta.transactionHint}` : ''}
        </p>
      </div>
      {loading && (
        <p className="inline-flex items-center gap-1 text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading…
        </p>
      )}
      {error && <p className="text-amber-800">{error}</p>}
      {!loading && !error && attributes.length === 0 && (
        <p className="text-slate-500">No attributes registered for this category.</p>
      )}
      <ul className="space-y-1" role="list">
        {attributes.map((attr) => (
          <li key={attr.id}>
            <button
              type="button"
              disabled={disabled}
              className="w-full text-left rounded-md border border-transparent px-2 py-1.5 hover:border-cyan-200 hover:bg-cyan-50 disabled:opacity-50"
              onClick={() =>
                onPickAttribute?.({
                  table: attr.table,
                  column: attr.column,
                  attribute: attr.id,
                  factType: attr.factType,
                  operator: 'equals',
                  value: '',
                })
              }
            >
              <span className="font-medium text-slate-800">{attr.label}</span>
              <span className="block text-[10px] text-slate-500 font-mono truncate">
                {attr.tableColumn}
              </span>
              {attr.sampleValues?.length > 0 && (
                <span className="block text-[10px] text-slate-400 truncate">
                  e.g. {attr.sampleValues.slice(0, 3).join(', ')}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
}
